/**
 * System Health Dashboard
 *
 * Displays real-time system health status with:
 * - Overall health indicator (green/yellow/red)
 * - Timeline of health checks
 * - Plain English issue descriptions
 * - Auto-recovery status
 */

import { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Badge,
  Spinner,
  Button,
  Alert,
  ProgressBar,
  ListGroup,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { FirebaseAuthContext } from 'context/FirebaseAuthContext';
import PageHeader from 'components/common/PageHeader';
import { db } from 'config/firebase';
import { doc, onSnapshot, collection, query, orderBy, limit, getDocs, getDoc } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faSync,
  faHeartbeat,
  faClock,
  faWrench,
  faEnvelope,
  faHistory,
  faShieldAlt,
  faServer
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { ServiceHealthCard } from './components/ServiceHealthCard';
import { SERVICE_CATEGORIES, getServicesByCategory } from './config/serviceRegistry';
import { useServiceHealth } from './hooks/useServiceHealth';
import { useDailyHealth } from './hooks/useDailyHealth';
import { UptimeTimeline } from './components/UptimeTimeline';

// Plain English descriptions for issue types
const ISSUE_DESCRIPTIONS = {
  stuck_enrichment_jobs: {
    title: 'Product Updates Delayed',
    description: 'Some product enrichment jobs are taking longer than expected to complete.',
    impact: 'New product titles, descriptions, or weights may not appear in Shopify until resolved.',
    action: 'The system is automatically recovering these jobs by marking them for retry.'
  },
  ibmi_task_failures: {
    title: 'Inventory Sync Issues',
    description: 'Some inventory updates from IBMi are failing to process.',
    impact: 'Product stock levels in the web store may not reflect the latest warehouse data.',
    action: 'These require manual investigation. Check the IBMi connection and task queue.'
  },
  ibmi_tasks_stuck_new: {
    title: 'Inventory Tasks Queued',
    description: 'Inventory sync tasks are waiting longer than expected to start processing.',
    impact: 'Minor delay in inventory updates, but they should process soon.',
    action: 'Usually resolves automatically. If persistent, the worker may need attention.'
  },
  shopify_push_errors: {
    title: 'Shopify Update Errors',
    description: 'Some product updates failed to sync to Shopify.',
    impact: 'Products may have outdated information in your Shopify store.',
    action: 'Review the failed items and retry the push, or check Shopify API limits.'
  },
  asin_verification_backlog: {
    title: 'Amazon Matching Backlog',
    description: 'Many products are waiting for Amazon ASIN verification.',
    impact: 'Products won\'t appear on Amazon until their ASINs are verified.',
    action: 'Review the verification queue and approve or reject pending matches.'
  },
  fc_analysis_stale: {
    title: 'Cost Analysis Outdated',
    description: 'The daily fulfillment cost analysis hasn\'t run recently.',
    impact: 'FC recommendations may not reflect the latest shipping cost data.',
    action: 'The scheduled job may have failed. Check the FC Optimizer logs.'
  },
  fc_analysis_errors: {
    title: 'Cost Analysis Had Errors',
    description: 'The last fulfillment cost analysis completed but encountered some errors.',
    impact: 'Some SKUs may not have updated recommendations.',
    action: 'Review the analysis logs to identify problematic SKUs.'
  },
  fc_analysis_never_run: {
    title: 'Cost Analysis Not Set Up',
    description: 'The fulfillment cost analysis has never been run.',
    impact: 'No FC recommendations are available.',
    action: 'The scheduled analysis should run daily at 3 AM. Trigger manually if needed.'
  }
};

// Status colors and icons
const STATUS_CONFIG = {
  // New status values (green/yellow/red from backend)
  green: {
    color: 'success',
    icon: faCheckCircle,
    label: 'All Systems Operational',
    bgClass: 'bg-success'
  },
  yellow: {
    color: 'warning',
    icon: faExclamationTriangle,
    label: 'Minor Issues Detected',
    bgClass: 'bg-warning'
  },
  red: {
    color: 'danger',
    icon: faTimesCircle,
    label: 'Critical Issues',
    bgClass: 'bg-danger'
  },
  // Legacy status values (for backwards compatibility)
  healthy: {
    color: 'success',
    icon: faCheckCircle,
    label: 'All Systems Operational',
    bgClass: 'bg-success'
  },
  warning: {
    color: 'warning',
    icon: faExclamationTriangle,
    label: 'Minor Issues Detected',
    bgClass: 'bg-warning'
  },
  degraded: {
    color: 'warning',
    icon: faExclamationTriangle,
    label: 'Performance Degraded',
    bgClass: 'bg-warning'
  },
  critical: {
    color: 'danger',
    icon: faTimesCircle,
    label: 'Critical Issues',
    bgClass: 'bg-danger'
  },
  unknown: {
    color: 'secondary',
    icon: faClock,
    label: 'Status Unknown',
    bgClass: 'bg-secondary'
  }
};

// Severity badge colors
const SEVERITY_BADGES = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'secondary'
};

export default function SystemHealth() {
  const { user, authLoading } = useContext(FirebaseAuthContext);
  const navigate = useNavigate();

  const [healthData, setHealthData] = useState(null);
  const [healerData, setHealerData] = useState(null);
  const [healerLogs, setHealerLogs] = useState([]);
  const [killSwitchData, setKillSwitchData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [healerRefreshing, setHealerRefreshing] = useState(false);
  const [killSwitchLoading, setKillSwitchLoading] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [error, setError] = useState(null);

  // Service health subscriptions for individual service status cards
  const { serviceHealth, loading: serviceLoading, error: serviceError } = useServiceHealth();

  // Daily health data for uptime timeline (90 days)
  const { dailyData, uptimeStats, getServiceUptime, loading: dailyLoading } = useDailyHealth(90);

  // Auth check
  useEffect(() => {
    if (authLoading) return;
    if (!user) return navigate('/');
  }, [authLoading, user, navigate]);

  // Real-time health data subscription
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'systemHealth', 'current'),
      (doc) => {
        if (doc.exists()) {
          setHealthData(doc.data());
        } else {
          setHealthData(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching health data:', err);
        setError('Failed to load health data');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Real-time healer data subscription
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'systemHealth', 'healer'),
      (doc) => {
        if (doc.exists()) {
          setHealerData(doc.data());
        }
      },
      (err) => {
        console.error('Error fetching healer data:', err);
      }
    );

    return () => unsubscribe();
  }, []);

  // Load healer logs
  useEffect(() => {
    const loadHealerLogs = async () => {
      try {
        const logsQuery = query(
          collection(db, 'systemHealth', 'healer', 'logs'),
          orderBy('timestamp', 'desc'),
          limit(10)
        );
        const snapshot = await getDocs(logsQuery);
        const logs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setHealerLogs(logs);
      } catch (err) {
        console.error('Error loading healer logs:', err);
      }
    };

    loadHealerLogs();
    // Refresh every 2 minutes
    const interval = setInterval(loadHealerLogs, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Real-time kill switch subscription
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'systemHealth', 'killSwitch'),
      (doc) => {
        if (doc.exists()) {
          setKillSwitchData(doc.data());
        } else {
          setKillSwitchData({ disabled: false });
        }
      },
      (err) => {
        console.error('Error fetching kill switch data:', err);
      }
    );

    return () => unsubscribe();
  }, []);

  // Load history
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const historyQuery = query(
          collection(db, 'systemHealth', 'history', 'snapshots'),
          orderBy('checkedAt', 'desc'),
          limit(24) // Last 24 checks (6 hours at 15 min intervals)
        );
        const snapshot = await getDocs(historyQuery);
        const historyData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setHistory(historyData);
      } catch (err) {
        console.error('Error loading history:', err);
      }
    };

    loadHistory();
    // Refresh history every 5 minutes
    const interval = setInterval(loadHistory, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Manual refresh with toast notifications
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FUNCTIONS_URL || ''}/systemHealthCheck`,
        { method: 'POST' }
      );
      if (!response.ok) throw new Error('Health check failed');

      // Success - data will update via subscription
      toast.success('Health check complete', {
        position: 'bottom-right',
        autoClose: 3000
      });
    } catch (err) {
      console.error('Refresh failed:', err);
      setError('Failed to refresh health data');
      toast.error('Failed to refresh health data', {
        position: 'bottom-right',
        autoClose: 5000
      });
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Manual healer trigger
  const handleRunHealer = useCallback(async () => {
    setHealerRefreshing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FUNCTIONS_URL || ''}/autoHealerTrigger`,
        { method: 'POST' }
      );
      if (!response.ok) throw new Error('Auto-healer failed');
      // Data will update via the subscription
    } catch (err) {
      console.error('Healer failed:', err);
      setError('Failed to run auto-healer');
    } finally {
      setHealerRefreshing(false);
    }
  }, []);

  // Toggle kill switch
  const handleToggleKillSwitch = useCallback(async () => {
    const newState = killSwitchData?.disabled ? true : false; // If currently disabled, enable (enabled=true)
    const action = newState ? 'enable' : 'disable';

    if (!newState && !window.confirm(
      '⚠️ WARNING: This will DISABLE all automated healing.\n\n' +
      'Only do this if you believe the automation is causing problems.\n\n' +
      'Are you sure you want to disable the Auto-Healer?'
    )) {
      return;
    }

    setKillSwitchLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FUNCTIONS_URL || ''}/autoHealerKillSwitch`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            enabled: newState,
            reason: `Manual ${action} via dashboard`,
            triggeredBy: user?.email || 'Dashboard User'
          })
        }
      );
      if (!response.ok) throw new Error('Failed to toggle kill switch');
    } catch (err) {
      console.error('Kill switch toggle failed:', err);
      setError(`Failed to ${action} automation`);
    } finally {
      setKillSwitchLoading(false);
    }
  }, [killSwitchData, user]);

  // Rollback recent actions
  const handleRollback = useCallback(async () => {
    const timeWindow = window.prompt(
      '⏪ ROLLBACK HEALING ACTIONS\n\n' +
      'This will undo recent auto-healer changes and DISABLE automation.\n\n' +
      'Enter the time window in minutes (e.g., 60 for last hour):',
      '60'
    );

    if (!timeWindow) return;

    const minutes = parseInt(timeWindow);
    if (isNaN(minutes) || minutes < 1 || minutes > 1440) {
      setError('Invalid time window. Please enter 1-1440 minutes.');
      return;
    }

    if (!window.confirm(
      `⚠️ CONFIRM ROLLBACK\n\n` +
      `This will:\n` +
      `1. DISABLE all automated healing\n` +
      `2. Undo all healing actions from the last ${minutes} minutes\n\n` +
      `Are you sure?`
    )) {
      return;
    }

    setRollbackLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FUNCTIONS_URL || ''}/autoHealerRollback`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            timeWindowMinutes: minutes,
            reason: 'Manual rollback via dashboard',
            triggeredBy: user?.email || 'Dashboard User'
          })
        }
      );

      if (!response.ok) throw new Error('Rollback failed');

      const result = await response.json();
      alert(
        `✅ Rollback Complete\n\n` +
        `Found: ${result.found} actions\n` +
        `Rolled back: ${result.rolledBack}\n` +
        `Failed: ${result.failed}\n` +
        `Skipped: ${result.skipped}\n\n` +
        `⚠️ Kill switch is now ACTIVE. Re-enable automation when ready.`
      );
    } catch (err) {
      console.error('Rollback failed:', err);
      setError('Failed to rollback healing actions');
    } finally {
      setRollbackLoading(false);
    }
  }, [user]);

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (authLoading || loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading system health...</p>
      </div>
    );
  }

  const status = healthData?.status || 'unknown';
  const statusConfig = STATUS_CONFIG[status];

  return (
    <>
      <PageHeader
        title="System Health"
        description="Monitor the health of all Merlin systems"
        className="mb-3"
      />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {serviceError && (
        <Alert variant="warning" dismissible>
          {serviceError}
        </Alert>
      )}

      {/* Main Status Card */}
      <Card className={`mb-4 border-0 shadow-sm`}>
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col xs="auto">
              <div
                className={`rounded-circle d-flex align-items-center justify-content-center ${statusConfig.bgClass}`}
                style={{ width: 80, height: 80 }}
              >
                <FontAwesomeIcon
                  icon={statusConfig.icon}
                  className="text-white"
                  size="2x"
                />
              </div>
            </Col>
            <Col>
              <h2 className={`mb-1 text-${statusConfig.color}`}>
                {statusConfig.label}
              </h2>
              <p className="text-muted mb-0">
                <FontAwesomeIcon icon={faClock} className="me-2" />
                Last checked: {formatRelativeTime(healthData?.checkedAt)}
              </p>
            </Col>
            <Col xs="auto" className="text-end">
              {/* Uptime Stats */}
              {uptimeStats.last90Days !== null && (
                <div className="mb-2">
                  <span className="fs-4 fw-bold text-success">{uptimeStats.last90Days}%</span>
                  <small className="text-muted d-block">90-day uptime</small>
                </div>
              )}
              <Button
                variant="outline-primary"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <FontAwesomeIcon
                  icon={faSync}
                  spin={refreshing}
                  className="me-2"
                />
                {refreshing ? 'Checking...' : 'Check Now'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 90-Day Uptime Timeline */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-light">
          <FontAwesomeIcon icon={faHistory} className="me-2" />
          90-Day Uptime History
          {uptimeStats.last7Days !== null && (
            <Badge bg="light" text="dark" className="ms-3">
              Last 7 days: {uptimeStats.last7Days}%
            </Badge>
          )}
        </Card.Header>
        <Card.Body>
          <UptimeTimeline
            dailyData={dailyData}
            days={90}
            loading={dailyLoading}
          />
        </Card.Body>
      </Card>

      {/* Service Status Grid */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-light">
          <FontAwesomeIcon icon={faServer} className="me-2" />
          Service Status
        </Card.Header>
        <Card.Body>
          {serviceLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading service status...
            </div>
          ) : (
            <>
              {/* Order Flow Services */}
              <h6 className="text-muted mb-3">
                {SERVICE_CATEGORIES.ORDER_FLOW.label}
              </h6>
              <Row>
                {getServicesByCategory('ORDER_FLOW').map((service) => (
                  <Col md={6} key={service.id}>
                    <ServiceHealthCard
                      service={service}
                      healthData={serviceHealth[service.id]}
                      serviceUptime={getServiceUptime(service.id, 30)}
                    />
                  </Col>
                ))}
              </Row>

              {/* Support Services */}
              <h6 className="text-muted mb-3 mt-4">
                {SERVICE_CATEGORIES.SUPPORT.label}
              </h6>
              <Row>
                {getServicesByCategory('SUPPORT').map((service) => (
                  <Col md={6} key={service.id}>
                    <ServiceHealthCard
                      service={service}
                      healthData={serviceHealth[service.id]}
                      serviceUptime={getServiceUptime(service.id, 30)}
                    />
                  </Col>
                ))}
              </Row>

              {/* Infrastructure */}
              {SERVICE_CATEGORIES.INFRASTRUCTURE && (
                <>
                  <h6 className="text-muted mb-3 mt-4">
                    {SERVICE_CATEGORIES.INFRASTRUCTURE.label}
                  </h6>
                  <Row>
                    {getServicesByCategory('INFRASTRUCTURE').map((service) => (
                      <Col md={6} key={service.id}>
                        <ServiceHealthCard
                          service={service}
                          healthData={serviceHealth[service.id]}
                          serviceUptime={getServiceUptime(service.id, 30)}
                        />
                      </Col>
                    ))}
                  </Row>
                </>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <Row>
        {/* Issues Panel */}
        <Col lg={8}>
          <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
              <span>
                <FontAwesomeIcon icon={faHeartbeat} className="me-2" />
                Current Status
              </span>
              {healthData?.totalIssues > 0 && (
                <Badge bg="secondary">{healthData.totalIssues} issue{healthData.totalIssues !== 1 ? 's' : ''}</Badge>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              {!healthData || healthData.totalIssues === 0 ? (
                <div className="text-center py-5">
                  <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
                  <h5 className="text-success">Everything is running smoothly!</h5>
                  <p className="text-muted mb-0">
                    All systems are operational. No issues detected.
                  </p>
                </div>
              ) : (
                <ListGroup variant="flush">
                  {healthData.issues?.map((issue, index) => {
                    const description = ISSUE_DESCRIPTIONS[issue.type] || {
                      title: issue.type,
                      description: issue.message,
                      impact: 'Unknown impact',
                      action: 'Contact support for assistance'
                    };

                    return (
                      <ListGroup.Item key={index} className="py-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-0">
                            <Badge bg={SEVERITY_BADGES[issue.severity]} className="me-2">
                              {issue.severity.toUpperCase()}
                            </Badge>
                            {description.title}
                          </h6>
                          {issue.autoRecoverable && (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Auto-healing enabled</Tooltip>}
                            >
                              <Badge bg="info">
                                <FontAwesomeIcon icon={faWrench} className="me-1" />
                                Auto-Heal
                              </Badge>
                            </OverlayTrigger>
                          )}
                        </div>
                        <p className="mb-2 text-muted small">{description.description}</p>
                        <Row className="small">
                          <Col md={6}>
                            <strong>Impact:</strong> {description.impact}
                          </Col>
                          <Col md={6}>
                            <strong>Action:</strong> {description.action}
                          </Col>
                        </Row>
                        {issue.count > 1 && (
                          <Badge bg="light" text="dark" className="mt-2">
                            {issue.count} affected items
                          </Badge>
                        )}
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
            </Card.Body>
          </Card>

          {/* Auto-Recovery Status */}
          {healthData?.autoRecovery && healthData.autoRecovery.recovered > 0 && (
            <Card className="mb-4 shadow-sm border-success">
              <Card.Header className="bg-success text-white">
                <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                Auto-Recovery Active
              </Card.Header>
              <Card.Body>
                <p className="mb-2">
                  <strong>{healthData.autoRecovery.recovered}</strong> stuck job{healthData.autoRecovery.recovered !== 1 ? 's were' : ' was'} automatically recovered in the last check.
                </p>
                <p className="text-muted small mb-0">
                  The system automatically detects jobs that are stuck in processing and resets them for retry.
                  This happens every 15 minutes.
                </p>
              </Card.Body>
            </Card>
          )}

          {/* Auto-Healer Service */}
          <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
              <span>
                <FontAwesomeIcon icon={faWrench} className="me-2" />
                Auto-Healer Service
              </span>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleRunHealer}
                disabled={healerRefreshing}
              >
                <FontAwesomeIcon
                  icon={faSync}
                  spin={healerRefreshing}
                  className="me-1"
                />
                {healerRefreshing ? 'Running...' : 'Run Now'}
              </Button>
            </Card.Header>
            <Card.Body>
              {healerData ? (
                <>
                  <Row className="mb-3">
                    <Col xs={6} md={3} className="text-center">
                      <h4 className="text-success mb-0">{healerData.results?.healed || 0}</h4>
                      <small className="text-muted">Healed</small>
                    </Col>
                    <Col xs={6} md={3} className="text-center">
                      <h4 className="text-secondary mb-0">{healerData.results?.skipped || 0}</h4>
                      <small className="text-muted">Skipped</small>
                    </Col>
                    <Col xs={6} md={3} className="text-center">
                      <h4 className="text-warning mb-0">{healerData.results?.escalated || 0}</h4>
                      <small className="text-muted">Escalated</small>
                    </Col>
                    <Col xs={6} md={3} className="text-center">
                      <h4 className="text-danger mb-0">{healerData.results?.errors?.length || 0}</h4>
                      <small className="text-muted">Errors</small>
                    </Col>
                  </Row>
                  <p className="text-muted small mb-0">
                    <FontAwesomeIcon icon={faClock} className="me-1" />
                    Last run: {formatRelativeTime(healerData.lastRun)}
                    {healerData.durationMs && ` (${Math.round(healerData.durationMs / 1000)}s)`}
                  </p>
                </>
              ) : (
                <p className="text-muted mb-0">Auto-healer has not run yet</p>
              )}
            </Card.Body>

            {/* Recent Healing Actions */}
            {healerLogs.length > 0 && (
              <>
                <Card.Header className="bg-white border-top py-2">
                  <small className="text-muted">Recent Healing Actions</small>
                </Card.Header>
                <ListGroup variant="flush" style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {healerLogs.map((log) => (
                    <ListGroup.Item key={log.id} className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>
                          <Badge
                            bg={log.status === 'success' ? 'success' :
                              log.status === 'escalated' ? 'warning' :
                                log.status === 'skipped' ? 'secondary' : 'danger'}
                            className="me-2"
                          >
                            {log.status}
                          </Badge>
                          <small>{log.action?.replace(/_/g, ' ')}</small>
                        </span>
                        <small className="text-muted">
                          {log.details?.sku || log.itemId}
                        </small>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </>
            )}
          </Card>
        </Col>

        {/* Right Sidebar */}
        <Col lg={4}>
          {/* Metrics Summary */}
          <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-light">
              <FontAwesomeIcon icon={faHeartbeat} className="me-2" />
              System Metrics
            </Card.Header>
            <Card.Body className="p-0">
              <ListGroup variant="flush">
                <ListGroup.Item className="d-flex justify-content-between">
                  <span>Stuck Enrichment Jobs</span>
                  <Badge bg={healthData?.metrics?.enrichment?.stuckJobs > 0 ? 'warning' : 'success'}>
                    {healthData?.metrics?.enrichment?.stuckJobs || 0}
                  </Badge>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between">
                  <span>Failed IBMi Tasks</span>
                  <Badge bg={healthData?.metrics?.ibmi?.failedTasks > 0 ? 'danger' : 'success'}>
                    {healthData?.metrics?.ibmi?.failedTasks || 0}
                  </Badge>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between">
                  <span>Shopify Push Errors (24h)</span>
                  <Badge bg={healthData?.metrics?.shopify?.pushErrors > 0 ? 'warning' : 'success'}>
                    {healthData?.metrics?.shopify?.pushErrors || 0}
                  </Badge>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between">
                  <span>ASIN Review Queue</span>
                  <Badge bg={healthData?.metrics?.asinVerification?.pendingReview > 500 ? 'warning' : 'secondary'}>
                    {healthData?.metrics?.asinVerification?.pendingReview || 0}
                  </Badge>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>

          {/* Health Timeline */}
          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <FontAwesomeIcon icon={faHistory} className="me-2" />
              Recent History
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 300, overflowY: 'auto' }}>
              {history.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <p className="mb-0">No history available yet</p>
                </div>
              ) : (
                <ListGroup variant="flush">
                  {history.slice(0, 12).map((snapshot, index) => {
                    const snapshotStatus = snapshot.status || 'unknown';
                    const config = STATUS_CONFIG[snapshotStatus];
                    return (
                      <ListGroup.Item
                        key={snapshot.id}
                        className="d-flex align-items-center py-2"
                      >
                        <FontAwesomeIcon
                          icon={config.icon}
                          className={`text-${config.color} me-3`}
                          style={{ width: 16 }}
                        />
                        <div className="flex-grow-1">
                          <small className="text-muted">
                            {formatTime(snapshot.checkedAt)}
                          </small>
                        </div>
                        {snapshot.totalIssues > 0 && (
                          <Badge bg="light" text="dark" className="ms-2">
                            {snapshot.totalIssues}
                          </Badge>
                        )}
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
            </Card.Body>
          </Card>

          {/* Kill Switch & Rollback Controls */}
          <Card className={`mt-4 shadow-sm ${killSwitchData?.disabled ? 'border-danger' : 'border-success'}`}>
            <Card.Header className={killSwitchData?.disabled ? 'bg-danger text-white' : 'bg-success text-white'}>
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
              {killSwitchData?.disabled ? '🛑 Automation DISABLED' : '✅ Automation Active'}
            </Card.Header>
            <Card.Body>
              {killSwitchData?.disabled ? (
                <>
                  <Alert variant="danger" className="mb-3">
                    <strong>Kill switch is active.</strong><br />
                    All automated healing is currently disabled.
                  </Alert>
                  {killSwitchData?.reason && (
                    <p className="small text-muted mb-2">
                      <strong>Reason:</strong> {killSwitchData.reason}
                    </p>
                  )}
                  <Button
                    variant="success"
                    size="sm"
                    className="w-100"
                    onClick={handleToggleKillSwitch}
                    disabled={killSwitchLoading}
                  >
                    {killSwitchLoading ? 'Enabling...' : '✅ Re-Enable Automation'}
                  </Button>
                </>
              ) : (
                <>
                  <p className="small text-muted mb-3">
                    Auto-healer is running normally every 30 minutes.
                  </p>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="w-100 mb-2"
                    onClick={handleToggleKillSwitch}
                    disabled={killSwitchLoading}
                  >
                    {killSwitchLoading ? 'Disabling...' : '🛑 Emergency Stop'}
                  </Button>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    className="w-100"
                    onClick={handleRollback}
                    disabled={rollbackLoading}
                  >
                    {rollbackLoading ? 'Rolling back...' : '⏪ Rollback Recent Actions'}
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>

          {/* Email Alerts Info */}
          <Card className="mt-4 shadow-sm">
            <Card.Header className="bg-light">
              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
              Alert Notifications
            </Card.Header>
            <Card.Body>
              <p className="small text-muted mb-2">
                Critical and high-priority issues are automatically sent to:
              </p>
              <ul className="small mb-0">
                <li>sam@topicz.com</li>
                <li>tbeaty@topicz.com</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}
